import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChartBox, cssVar } from "../ChartPrimitives/ChartPrimitives";
import { formatAmountDisplay } from "../../../TransactionsTableComponent/TransactionsTableComponent";

function formatMaybeCurrency(n, currencyCode) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return String(n ?? "");
  try {
    if (!currencyCode) {
      return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return formatAmountDisplay(num, { currencyCode });
  } catch { return num.toFixed(2); }
}

function topLevelTagsOfTx(tx) {
  // tagPath can be array of strings ("food/groceries", "home/rent")
  const raw = Array.isArray(tx?.tagPath) ? tx.tagPath : (tx?.tagPath ? [tx.tagPath] : []);
  const tops = new Set();
  for (const path of raw) {
    const s = String(path || "").trim();
    const top = (s.split("/")[0] || "Uncategorized").trim();
    if (top) tops.add(top);
  }
  if (!tops.size) tops.add("Uncategorized");
  return Array.from(tops);
}

function rollupByTopTags(items, { sign = -1, limit = 8, excludeTopLevels = [] } = {}) {
  const ex = new Set((excludeTopLevels || []).map(s => String(s || "").toLowerCase()));
  const acc = new Map();
  for (const tx of items || []) {
    const amt = Number(tx?.amount || 0);
    if (!Number.isFinite(amt) || amt === 0) continue;

    if (sign < 0 && amt >= 0) continue;
    if (sign > 0 && amt <= 0) continue;

    const tops = topLevelTagsOfTx(tx).filter(t => !ex.has(t.toLowerCase()));
    if (!tops.length) continue;

    const share = Math.abs(amt) / tops.length; // equal split across all top-level tags
    for (const t of tops) acc.set(t, (acc.get(t) || 0) + share);
  }
  const sorted = [...acc.entries()].sort((a, b) => b[1] - a[1]);
  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  const otherTotal = tail.reduce((s, [, v]) => s + v, 0);

  const labels = head.map(([k]) => k);
  const values = head.map(([, v]) => v);
  if (otherTotal > 0) { labels.push("Other"); values.push(otherTotal); }
  return { labels, values };
}

export default function DonutTagPair({
  items,
  currencyCode,
  excludeTopLevels = ["Transfer", "Internal"],
  leftTitle = "Spend by tag",
  rightTitle = "Income by tag",
  height = 220,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const spend = useMemo(
    () => rollupByTopTags(items, { sign: -1, limit: 8, excludeTopLevels }),
    [items, excludeTopLevels]
  );
  const income = useMemo(
    () => rollupByTopTags(items, { sign: +1, limit: 8, excludeTopLevels }),
    [items, excludeTopLevels]
  );

  const showSpend = (spend.labels?.length || 0) >= 2;
  const showIncome = (income.labels?.length || 0) >= 2;
  if (!showSpend && !showIncome) return null;

  const paletteAt = (i) => cssVar(`--pp-cat-${i}`, "#8884d8");
  const donutData = (series) => ({
    labels: series.labels,
    datasets: [{
      data: series.values,
      backgroundColor: series.labels.map((lbl, i) =>
        lbl === "Other" ? cssVar("--pp-cat-other", "#9ca3af") : paletteAt(i)
      ),
      borderWidth: 0
    }]
  });

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 10, color: cssVar("--pp-chart-tick", "#6b7280") } },
      tooltip: {
        callbacks: {
          // show spend as negative for clarity
          label: (ctx) => {
            const sign = (ctx.chart?.config?.data?.labels === spend.labels) ? -1 : +1;
            return ` ${ctx.label}: ${formatMaybeCurrency(sign * Math.abs(ctx.parsed), currencyCode)}`;
          }
        }
      }
    },
    onClick: (_evt, elements, chart) => {
      if (!elements || !elements.length) return;
      const idx = elements[0].index;
      const label = chart?.data?.labels?.[idx];
      if (!label || label === "Other") return;
      const sp = new URLSearchParams(location.search);
      sp.set("tag", label);
      navigate({ pathname: location.pathname, search: sp.toString() });
    }
  };

  return (
    <>
      {showSpend && (
        <div className="col-12 col-md-6">
          <ChartBox
            title={leftTitle}
            type="doughnut"
            data={donutData(spend)}
            options={donutOptions}
            height={height}
          />
        </div>
      )}
      {showIncome && (
        <div className="col-12 col-md-6">
          <ChartBox
            title={rightTitle}
            type="doughnut"
            data={donutData(income)}
            options={donutOptions}
            height={height}
          />
        </div>
      )}
    </>
  );
}
